import React, { useState, useEffect } from "react";
import { Flex, Icon, Link, Badge, Box, VStack, Heading, Text, useColorModeValue, useBreakpointValue, SimpleGrid, HStack } from "@chakra-ui/react";
import axios from "axios";
import { Batch, Tutor } from "../Common/types";
import { FaUser, FaClock, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import { utcTimeToLocalPretty } from '../../utils/dates';

const TutorBatches = () => {
  const [batchs, setBatchs] = useState<Batch[]>([]);
  const [currentTutor, SetCurrentTutor] = useState<Tutor>();
  const batchCardBg = useColorModeValue("blue.50", "blue.700");
  const textColor = useColorModeValue("blue.800", "blue.50");
  const bannerColor = useColorModeValue("yellow.100", "yellow.700");
  const headlineColor = useColorModeValue("teal.600", "teal.100");

  const showBanner = useBreakpointValue({ base: false, md: true }); // Hide footer on smaller screens
  

  const fetchBatchs = async () => {
    if (!currentTutor) return;

    try {
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await axios.get(`${apiurl}/tutors/${currentTutor.tutor_id}/batch-details`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setBatchs(response.data);
    } catch (error) {
      console.error("Failed to load enrolled batches", error);
    }
  };

  useEffect(() => {
    const fetchCurrentTutor = async () => {
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        const response = await axios.get(`${apiurl}/tutor`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        SetCurrentTutor(response.data);
      } catch (error) {
        console.error("Error fetching Tutor Info:", error);
      }
    };
    fetchCurrentTutor();
  }, []);

  useEffect(() => {
    if (currentTutor) {
      fetchBatchs();
    }
  }, [currentTutor]);

  return (

    <Box >
      {showBanner && (
    <Box bgColor={bannerColor} p={4} height={"70px"}>
      <HStack>
        <Heading   size={"sm"}>Hello  {currentTutor?.given_name} ! </Heading>
        <Text  fontWeight={"normal"} fontSize={"md"}>Checkout your batches for quick meeting links and more batch related information.</Text>      
        </HStack>
    </Box>
          )}
          {!showBanner && (
          <Box p={4} bgColor={bannerColor} height={"50px"}>
            <VStack>
              <Text  fontWeight={"semibold"} fontSize={"md"}>My Batches</Text>      
              </VStack>
          </Box>
          )}
    <Box as="main" mx="auto" maxWidth="1200px" p={4} color={textColor}>

    <SimpleGrid columns={{ base: 1, sm: 1, md: 3 }} spacing={6} mt={10}>
      {batchs.map((batch) => (
        <Box
          key={batch.id}
          p={6}
          bg={batchCardBg}
          borderRadius="lg"
          boxShadow="xl"
          borderWidth="1px"
          borderColor="gray.200"
          transition="0.3s"
          _hover={{ transform: "translateY(-4px)", boxShadow: "2xl" }}
        >
          {/* Course Title */}
          <Heading size="md" mb={2} color={headlineColor}>{batch.course_title}</Heading>

          {/* Tutor Name */}
          <Flex align="center" gap={2} mb={2}>
            <Icon as={FaUser} color="blue.500" />
            <Text fontSize="sm"><strong>Tutor:</strong> {batch.tutor_name || "Not Assigned"}</Text>
          </Flex>

          {/* Frequency */}
          <Badge colorScheme="purple" mb={3} fontSize="0.8em">
            {batch.frequency}
          </Badge>

          {/* Time Details */}
          <Flex align="center" gap={2} mb={2}>
            <Icon as={FaClock} color="orange.500" />
            <Text fontSize="sm">
              <strong>Time:</strong> {utcTimeToLocalPretty(batch.start_time)} - {utcTimeToLocalPretty(batch.end_time)}
            </Text>
          </Flex>

          {/* Location */}
          <Flex align="center" gap={2} mb={2}>
            <Icon as={FaMapMarkerAlt} color="red.500" />
            <Text fontSize="sm">
              <strong>Location:</strong> {batch.location || "N/A"}
            </Text>
          </Flex>

          {/* Start & End Date */}
          <Text fontSize="sm" mb={1}><strong>Start Date:</strong> {batch.start_date}</Text>
          <Text fontSize="sm" mb={3}><strong>End Date:</strong> {batch.end_date}</Text>

          {/* Meeting Link */}
          {batch.meeting_link && (
            <Flex align="center" gap={2} mb={3}>
              <Icon as={FaVideo} color="green.500" />
              <Link href={batch.meeting_link} color="blue.500" isExternal>
                Join Meeting
              </Link>
            </Flex>
          )}

          {/* Active Status */}
          <Badge colorScheme={batch.is_active ? "green" : "red"}>
            {batch.is_active ? "Active" : "Inactive"}
          </Badge>
        </Box>
      ))}
    </SimpleGrid>

    </Box>
    </Box>
  );
};

export default TutorBatches;
